import { router } from 'expo-router';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ContextMenuAction } from 'react-native-context-menu-view';

import { useDeleteSubscription } from '@/api/hooks/paymentAccountsHooks';
import { Subscription, SubscriptionStatus } from '@/api/models/paymentAccountsModels';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { encryptObject } from '@/utils/crypto';
import { formatCurrency } from '@/utils/currencyFormat';
import { clearTimezone, formatDateYear } from '@/utils/dateFormats';
import { i18n } from '@/utils/i18n';
import { paymentTypeToShow } from '@/utils/paymentTypeFormat';

import DotsLoader from '../DotsLoader';
import ContextMenu from '../messages/ContextMenu.web';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface SubscriptionItemViewProps {
  subscriptionItem: Subscription;
  amountColor?: string;
  onDelete: () => void;
}

const SubscriptionItemView = ({
  subscriptionItem: item,
  amountColor = 'green',
  onDelete
}: SubscriptionItemViewProps) => {
  const { showSuccess } = useEndFlowModal();
  const { mutateAsync: deleteSubscription, isPending: isPendingDelete } = useDeleteSubscription();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const actions: ContextMenuAction[] = [
    {
      title: 'Edit Subscription',
      icon: 'edit'
    },
    {
      title: 'Cancel Subscription',
      iconColor: 'red',
      icon: 'delete'
    }
  ];

  const handleCancel = () => {
    deleteSubscription(item.id.toString(), {
      onSuccess: () => {
        setShowConfirmationModal(false);
        showSuccess({
          title: i18n.t('end_flow.success_title'),
          message: i18n.t('donation.subscription_deleted')
        });
        onDelete();
      },
      onError: (e) => {
        console.error(e);
      }
    });
  };

  const statusToShow = (): { label: string; color: string } => {
    switch (item.status) {
      case SubscriptionStatus.ACTIVE:
        return {
          label: 'Active',
          color: 'green'
        };

      case SubscriptionStatus.CLOSED:
        return {
          label: 'Closed',
          color: 'red'
        };
      case SubscriptionStatus.INACTIVE:
        return {
          label: 'Inactive',
          color: 'red'
        };
      case SubscriptionStatus.PENDING:
        return {
          label: 'Pending',
          color: 'yellow'
        };
      default:
        return {
          label: 'No Status',
          color: 'gray'
        };
    }
  };

  return (
    <View style={styles.mainContainer} pointerEvents={isPendingDelete ? 'none' : 'auto'}>
      <View>
        <View style={styles.npoNameContainer}>
          <Text fontWeight="900" style={styles.npoName}>
            {item.nonProfitOrg.name}
          </Text>
        </View>
        <Text>
          {i18n.t('donation.subscription_billing_period', {
            PERIOD: item.billingPeriod
          })}
        </Text>
        <View style={styles.infoContainer}>
          <Text>{`${i18n.t('donation.status')}: `}</Text>
          <Text style={{ color: statusToShow().color }}>{statusToShow().label}</Text>
        </View>
        <View style={styles.infoContainer}>
          <Text>{`${i18n.t('donation.payment_method')}: `}</Text>
          <Text>{paymentTypeToShow(item.paymentType)}</Text>
        </View>
        {item.status !== SubscriptionStatus.CLOSED && (
          <Text>
            {i18n.t('donation.subscription_next_due_date', {
              DATE: formatDateYear(clearTimezone(item.dueDate))
            })}
          </Text>
        )}
      </View>
      <View style={styles.amountMenuContainer}>
        {isPendingDelete && <DotsLoader />}
        <Text style={{ color: amountColor }}>{formatCurrency(parseFloat(item.amount))}</Text>
        {item.status !== SubscriptionStatus.CLOSED && (
          <ContextMenu
            disabled={false}
            longPressable={false}
            actions={actions}
            showMenuPadding={1.5}
            onPress={async (actionTitle) => {
              switch (actionTitle) {
                case 'Edit Subscription': {
                  const payload = {
                    subscriptionItem: JSON.stringify(item)
                  };
                  const encrypted = await encryptObject(payload);

                  router.navigate({
                    pathname: '/donationPayment/editSubscription',
                    params: {
                      payload: encodeURIComponent(encrypted)
                    }
                  });
                  break;
                }
                case 'Cancel Subscription': {
                  setShowConfirmationModal(true);
                  break;
                }
                default:
                  break;
              }
            }}>
            <View style={styles.dotsContainer}>
              <View style={styles.dot} />
              <View style={styles.dot} />
              <View style={styles.dot} />
            </View>
          </ContextMenu>
        )}
      </View>
      <ConfirmWarningModal
        message={i18n.t('donation.subscription_cancel_confirm_message')}
        showPicker={showConfirmationModal}
        loading={isPendingDelete}
        onClosePicker={() => setShowConfirmationModal(false)}
        onConfirmTapped={handleCancel}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  amountMenuContainer: {
    flexDirection: 'row',
    gap: 15,
    alignItems: 'center'
  },

  npoNameContainer: {
    flexDirection: 'row',
    flex: 1,
    width: '100%'
  },

  npoName: {
    flex: 1,
    fontSize: 16,
    color: 'black',
    flexWrap: 'wrap'
  },

  mainContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18
  },

  dotsContainer: {
    flexDirection: 'row',
    gap: 5,
    padding: 5
  },

  dot: {
    width: 5,
    height: 5,
    borderRadius: 5 / 2,
    backgroundColor: 'black'
  },

  infoContainer: {
    flexDirection: 'row',
    gap: 3
  }
});

export default SubscriptionItemView;
